import { Link, createFileRoute } from "@tanstack/react-router"
import {
  AlignLeft,
  ArrowRight,
  Package,
  Palette,
  Table2,
  TextCursorInput,
  ToggleLeft,
  Type,
} from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { PageHeader } from "@/components/ui/PageHeader"

export const Route = createFileRoute("/design/")({
  component: DesignSystemIndex,
})

const showcasePages = [
  {
    title: "Logo & Branding",
    description: "View all logo variations, animations, and brand guidelines",
    href: "/design/logo-showcase",
    icon: Palette,
    status: "ready",
  },
  {
    title: "Tables",
    description: "Borderless table design with sorting, filtering, and advanced features",
    href: "/design/table-showcase",
    icon: Table2,
    status: "ready",
  },
  {
    title: "Button States",
    description: "Button component disabled states implementation from Figma design",
    href: "/design/button-disabled-showcase",
    icon: ToggleLeft,
    status: "ready",
  },
  {
    title: "Typography",
    description: "Font scales, text styles, and typography guidelines",
    href: "/design/typography",
    icon: Type,
    status: "ready",
  },
  {
    title: "Input Fields",
    description: "Input component with labels, helper text, icons, and error states",
    href: "/design/input-showcase",
    icon: TextCursorInput,
    status: "ready",
  },
  {
    title: "Textarea",
    description: "Multi-line text input with consistent design and states",
    href: "/design/textarea-showcase",
    icon: AlignLeft,
    status: "ready",
  },
  {
    title: "Components",
    description: "Complete UI component library with examples",
    href: "/design/components",
    icon: Package,
    status: "coming-soon",
  },
]

function DesignSystemIndex() {
  return (
    <div className="bg-background min-h-screen p-8">
      <div className="mx-auto max-w-6xl">
        <PageHeader
          title="Shelfi Design System"
          description="Explore the design components, patterns, and guidelines that make up the Shelfi experience"
        />

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {showcasePages.map((page) => {
            const Icon = page.icon
            const isReady = page.status === "ready"

            return (
              <Card
                key={page.href}
                className={isReady ? "transition-shadow hover:shadow-lg" : "opacity-60"}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-lg">
                        <Icon className="text-primary h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{page.title}</CardTitle>
                        {!isReady && (
                          <span className="mt-1 inline-flex items-center rounded-full bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400">
                            Coming Soon
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <CardDescription className="mt-2">{page.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  {isReady ? (
                    <Link
                      to={page.href}
                      className="text-primary inline-flex items-center text-sm font-medium hover:underline"
                    >
                      View Showcase
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  ) : (
                    <span className="text-sm text-neutral-500">In Development</span>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>

        <div className="mt-12 rounded-lg bg-neutral-50 p-6 dark:bg-neutral-900">
          <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 text-lg font-semibold">
            About the Design System
          </h2>
          <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-300">
            The Shelfi Design System provides a consistent visual language and component library for
            building user interfaces. It ensures brand consistency, improves development speed, and
            creates a cohesive user experience across all touchpoints.
          </p>
          <div className="mt-4 grid gap-4 text-sm md:grid-cols-3">
            <div>
              <h3 className="font-medium text-neutral-900 dark:text-neutral-100">Foundation</h3>
              <p className="mt-1 text-neutral-600 dark:text-neutral-400">
                Colors, typography, spacing, and core design principles
              </p>
            </div>
            <div>
              <h3 className="font-medium text-neutral-900 dark:text-neutral-100">Components</h3>
              <p className="mt-1 text-neutral-600 dark:text-neutral-400">
                Reusable UI elements built with React and Tailwind CSS
              </p>
            </div>
            <div>
              <h3 className="font-medium text-neutral-900 dark:text-neutral-100">Patterns</h3>
              <p className="mt-1 text-neutral-600 dark:text-neutral-400">
                Best practices for common UI scenarios and workflows
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
